import React from "react";
import { STATE_TONE } from "./priceCheck";
import { formatPrice } from "./priceCheckCopy";
import "./price-check.scss";

// Horizontal position (0–100) of every value on one shared scale: the cheapest
// listings, the median and the listing's own price, padded so the ends breathe.
const scale = (values, padRatio) => {
  const nums = values.filter((v) => Number.isFinite(v));
  const lo = Math.min(...nums);
  const hi = Math.max(...nums);
  const pad = (hi - lo || hi || 1) * padRatio;
  const min = lo - pad;
  const span = hi + pad - min;
  return (v) => ((v - min) / span) * 100;
};

const pct = (n) => `${n.toFixed(2)}%`;
// Labels near an edge anchor inwards so they never clip.
const anchor = (p, edge = 12) => (p < edge ? "start" : p > 100 - edge ? "end" : "mid");

/**
 * Chart A — range bar. The green zone is the cheapest five on the marketplace;
 * the ringed marker is the listing's own price in its state colour.
 * `compact` is the slim row version used in the Overview list.
 */
export default function PriceRangeBar({ check, currency, compact = false, className = "" }) {
  const list = check?.lowestList || [];
  if (list.length < 2 || check.price === null) return null;
  const a = list[0];
  const b = list[list.length - 1];
  const ours = check.price;
  const pos = scale([a, b, ours, check.median], compact ? 0.12 : 0.06);
  const left = pos(a);
  const width = Math.max(pos(b) - left, 1.5);
  const oursAt = pos(ours);
  const tone = STATE_TONE[check.state] || "neutral";
  const fmt = (v) => formatPrice(v, currency);
  const label = `Your price ${fmt(ours)}; the ${list.length} cheapest on ${check.label} range from ${fmt(a)} to ${fmt(b)}.`;
  const vars = {
    "--pc-zone-left": pct(left),
    "--pc-zone-width": pct(width),
    "--pc-zone-mid": pct(left + width / 2),
    "--pc-zone-end": pct(left + width),
    "--pc-you": pct(oursAt),
  };
  const classes = ["pcbar", compact ? "pcbar--compact" : "", `pc-tone--${tone}`, className].filter(Boolean).join(" ");

  if (compact) {
    return (
      <span className={classes} role="img" aria-label={label} style={vars}>
        <span className="pcbar__line" />
        <span className="pcbar__band" />
        <span className="pcbar__pin" />
      </span>
    );
  }

  return (
    <figure className={classes} role="img" aria-label={label} style={vars}>
      <span className="pcbar__caption">{`Top ${list.length}`}</span>
      <span className="pcbar__track" />
      <span className="pcbar__zone" />
      <span className="pcbar__marker" />
      <span className={`pcbar__tick pcbar__tick--lo is-${anchor(left, 8)}`}>{fmt(a)}</span>
      <span className={`pcbar__tick pcbar__tick--hi is-${anchor(left + width, 8)}`}>{fmt(b)}</span>
      <span className={`pcbar__you is-${anchor(oursAt)}`}>{`You ${fmt(ours)}`}</span>
    </figure>
  );
}
